const DROPDOWN_OFFSET = 8;
const DROPDOWN_MAX_HEIGHT = 350;
const DROPDOWN_Z_INDEX = '1100';

function getDropdownHeight(dropdownList) {
    return dropdownList.scrollHeight || dropdownList.getBoundingClientRect().height || 0;
}

function getViewportHeight() {
    return window.visualViewport ? window.visualViewport.height : window.innerHeight;
}

export function getDropdownLayout({
    dropdownHeight,
    scrollX,
    scrollY,
    toggleRect,
    viewportHeight,
}) {
    const preferredHeight = Math.min(dropdownHeight, DROPDOWN_MAX_HEIGHT);
    const spaceAbove = Math.max(toggleRect.top - DROPDOWN_OFFSET, 0);
    const spaceBelow = Math.max(viewportHeight - toggleRect.bottom - DROPDOWN_OFFSET, 0);
    const shouldOpenUp = spaceBelow < preferredHeight && spaceAbove > spaceBelow;
    const maxHeight = Math.min(DROPDOWN_MAX_HEIGHT,
        shouldOpenUp ? spaceAbove : spaceBelow);
    const renderedHeight = Math.min(preferredHeight, maxHeight);

    return {
        left: scrollX + toggleRect.left,
        width: toggleRect.width,
        maxHeight,
        top: scrollY + (shouldOpenUp ? toggleRect.top - renderedHeight : toggleRect.bottom),
    };
}

export default function watchSelectDropdownPosition(dropdownList, toggle) {
    let animationFrameId = null;
    let timeoutId = null;
    let resizeObserver = null;
    const visualViewport = window.visualViewport;
    const repositionDropdown = () => {
        const layout = getDropdownLayout({
            dropdownHeight: getDropdownHeight(dropdownList),
            scrollX: window.scrollX || window.pageXOffset,
            scrollY: window.scrollY || window.pageYOffset,
            toggleRect: toggle.getBoundingClientRect(),
            viewportHeight: getViewportHeight(),
        });

        dropdownList.style.zIndex = DROPDOWN_Z_INDEX;
        dropdownList.style.left = `${layout.left}px`;
        dropdownList.style.width = `${layout.width}px`;
        dropdownList.style.maxHeight = `${layout.maxHeight}px`;
        dropdownList.style.top = `${layout.top}px`;
    };
    const cancelScheduledReposition = () => {
        if (animationFrameId !== null && window.cancelAnimationFrame) {
            window.cancelAnimationFrame(animationFrameId);
            animationFrameId = null;
        }

        if (timeoutId !== null) {
            window.clearTimeout(timeoutId);
            timeoutId = null;
        }
    };
    const scheduleReposition = () => {
        cancelScheduledReposition();

        if (window.requestAnimationFrame) {
            animationFrameId = window.requestAnimationFrame(() => {
                animationFrameId = null;
                repositionDropdown();
            });
            return;
        }

        timeoutId = window.setTimeout(() => {
            timeoutId = null;
            repositionDropdown();
        }, 0);
    };
    const handleViewportChange = () => scheduleReposition();

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener(
        'scroll', handleViewportChange, true,
    );

    if (visualViewport) {
        visualViewport.addEventListener('resize', handleViewportChange);
        visualViewport.addEventListener('scroll', handleViewportChange);
    }

    if (window.ResizeObserver) {
        resizeObserver = new window.ResizeObserver(() => {
            scheduleReposition();
        });
        resizeObserver.observe(dropdownList);
    }

    scheduleReposition();

    return () => {
        window.removeEventListener('resize', handleViewportChange);
        window.removeEventListener(
            'scroll', handleViewportChange, true,
        );
        if (visualViewport) {
            visualViewport.removeEventListener('resize', handleViewportChange);
            visualViewport.removeEventListener('scroll', handleViewportChange);
        }
        if (resizeObserver) {
            resizeObserver.disconnect();
        }
        cancelScheduledReposition();
    };
}

export function watchSelectDropdownPositionByComponent(
    dropdownList,
    component,
    prepareDropdown,
) {
    const toggle = component && component.$refs ? component.$refs.toggle : null;

    if (!toggle) {
        return undefined;
    }

    if (prepareDropdown) {
        prepareDropdown(dropdownList);
    }

    return watchSelectDropdownPosition(dropdownList, toggle);
}
